import { Search } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Client-side search over a loaded list: case-insensitive, every word must appear in one of the
 * item's searchable fields. Returns the filtered items and the query state.
 */
export function useListSearch<T>(
  items: T[],
  fields: (item: T) => Array<string | null | undefined>,
) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return items;
    return items.filter((item) => {
      const haystack = fields(item).filter(Boolean).join(' ').toLowerCase();
      return words.every((w) => haystack.includes(w));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `fields` is a stable accessor in practice
  }, [items, query]);
  return { query, setQuery, filtered };
}

/**
 * The studio's list toolbar: a pill search field on the left and actions (e.g. "New …") on the
 * right. `label` names what is searched ("Search agents").
 */
export function ListSearch({
  value,
  onChange,
  label,
  placeholder,
  actions,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <div className="relative w-full max-w-md">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? label}
          aria-label={label}
          className="pl-9"
        />
      </div>
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** One table row (or block) saying nothing matched the search. */
export function NoMatches({ query }: { query: string }) {
  return (
    <p className="text-muted-foreground py-8 text-center text-sm">Nothing matches “{query}”.</p>
  );
}
